import useSorting from "../../../../hooks/useSorting";
import PaperClip from "../../../../assets/icons/paperclip.svg";
import { dateFormat } from "../../../../services/Utils/functionService";
import useTooltip from "../../../../hooks/useTooltip";
import { usePartnerRequestsStore2 } from "../../../../stores/Cac/Partner/partnerRequests";
import { usePartnerRequestDetailsStore } from "../../../../stores/Cac/Partner/partnerRequestDetails";
import { usePartnerMissionStore } from "../../../../stores/Cac/Partner/partnerMission";
import {
  handleAllCheckboxChangeService,
  handleCheckboxChangeService,
} from "../../../../services/Api/requestService";
import SelectedUsersRequest from "../Utils/SelectedUsersRequest";
import { useEffect, useState } from "react";
import {
  assignUserToDocRequest,
  getUserAssignToDocRequest,
  updateRequestState,
} from "../../../../services/Api/customerService";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showToastError, showToastSuccess } from "../../../../services/Toasts/toasts";
import message from "../../../../assets/icons/text-message.png";
import SelectState from "../Utils/SelectState";
import { useUtilsStore } from "../../../../stores/Utils/selects";

export const PartnerRequestTable = () => {
  const { t } = useTranslation();
  const {
    requests,
    requestIds,
    setRequestIds,
    setRequestId,
    editStatus,
    setOpenDetails,
    displayAllRequests,
    affectedUsers,
    setAffectedUsers,
    selectedUserToAffected,
    setSelectedUserToAffected,
    docRequestUserAssign,
    setDocRequestUserAssign,
    setEditStatus,
    setUpdateRequest,
    updateRequest,
    toggleRequestAssignment,
  } = usePartnerRequestsStore2();
  const { renewable } = usePartnerMissionStore();
  const { setOpenDocuments } = usePartnerRequestDetailsStore();
  const { idCustomer, idMission } = useParams();
  const { selectedState, setSelectedState } = useUtilsStore();

  // Hooks
  const [sortedData, handleSort] = useSorting(requests);
  const [tooltipState, handleMouseEnter, handleMouseLeave] = useTooltip();
  const [loading, setLoading] = useState(false);
  
  // State pour le tri
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const resetState = () => {
    setLoading(false);
    setUpdateRequest(!updateRequest);
    setRequestIds([]);
    setEditStatus(false);
    setSelectedState(0);
  };

  // Fonction de tri personnalisée
  const handleCustomSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    handleSort(field);
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortDirection === "asc") {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      );
    }
  };

  // This is for checkbox for change state of requests
  const handleCheckboxChange = (id: any): void => {
    handleCheckboxChangeService(id, requestIds, setRequestIds);
  };

  const handleAllCheckboxChange = () => {
    handleAllCheckboxChangeService(requests, requestIds, setRequestIds);
  };

  useEffect(() => {
    if (affectedUsers && selectedUserToAffected?.id && selectedUserToAffected.id !== 0) {
      getUserAssignToDocRequest(
        Number(idCustomer),
        Number(idMission),
        selectedUserToAffected.id
      )
        .then((data) => {
          setDocRequestUserAssign(data);
        })
        .catch(() => showToastError(t("common.error")));
      }
  }, [affectedUsers, selectedUserToAffected]);

  // Permet 
  const handleSendAssign = () => {
    assignUserToDocRequest(
      Number(idCustomer),
      Number(idMission),
      selectedUserToAffected.id,
      docRequestUserAssign
    )
      .then(() => {
        setAffectedUsers(false);
        setDocRequestUserAssign([]);
        setSelectedUserToAffected({ id: 0, fullName: "" });
        showToastSuccess(t("partner.usersAssigned"));
      })
      .catch(() => showToastError(t("common.error")));
  };

  useEffect(() => {
    setAffectedUsers(false);
  }, [idMission]);

  // Function for change state of requests
  const updateSelectedRequestStates = async () => {
    try {
      setLoading(true);

      const data = requestIds.map((requestId) => ({
        idDocRequest: requestId,
        state: selectedState,
      }));

      await updateRequestState(Number(idCustomer), Number(idMission), data);

      showToastSuccess(t("partner.statusUpdated"));
      resetState();
    } catch (error) {
      showToastError(t("partner.statusUpdateError"));
      resetState();
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {/* Header avec système de tri moderne */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 py-3 sm:px-5 2xl:py-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Actions à gauche - affectedUsers OU editStatus */}
          <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:gap-4">
            {affectedUsers && (
              <>
                <SelectedUsersRequest />
                <button
                  onClick={handleSendAssign}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg bg-green-500 px-3 py-2 text-sm text-white transition-colors duration-200 hover:bg-green-600 sm:px-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {t("common.confirm")}
                </button>
                <button
                  onClick={() => {
                    setAffectedUsers(!affectedUsers);
                    setSelectedUserToAffected({ id: 0, fullName: "", eMail: "" });
                    setDocRequestUserAssign([]);
                  }}
                  className="flex min-h-10 cursor-pointer items-center gap-2 rounded-lg bg-red-500 px-3 py-2 text-sm text-white transition-colors duration-200 hover:bg-red-600 sm:px-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t("common.cancel")}
                </button>
              </>
            )}
            
            {editStatus && (
              <>
                <SelectState />
                <button
                  type="button"
                  onClick={() => {
                    if (selectedState === 0) {
                      showToastError(t("partner.selectStatus"));
                    } else {
                      updateSelectedRequestStates();
                    }
                  }}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex cursor-pointer items-center gap-2 ${
                    !loading
                      ? "bg-green-500 text-white hover:bg-green-600 shadow-lg hover:shadow-xl transform hover:scale-105"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  {!loading ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {t("common.confirm")}
                    </>
                  ) : (
                    <svg
                      aria-hidden="true"
                      role="status"
                      className="inline w-4 h-4 animate-spin text-gray-200"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="#1C64F2"
                      />
                    </svg>
                  )}
                </button>
                <button
                  className="px-4 py-2 bg-red-500 text-white cursor-pointer rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center gap-2"
                  onClick={() => {
                    setEditStatus(!editStatus);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t("common.cancel")}
                </button>
              </>
            )}
          </div>

          {/* Filtres et tri */}
          <div className="hidden md:flex w-full items-center gap-2 overflow-x-auto pb-1 lg:w-auto lg:pb-0">
              <span className="flex-shrink-0 text-sm font-medium text-gray-600">{t("common.sortBy")}</span>
              
              {/* Bouton de tri par ID */}
              <button 
                onClick={() => handleCustomSort("fakeId")}
                className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                  sortField === "fakeId" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("common.id")}</span>
                {getSortIcon("fakeId")}
              </button>
              
              {/* Bouton de tri par catégorie */}
              <button 
                onClick={() => handleCustomSort("CategoryDocRequest.name")}
                className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                  sortField === "CategoryDocRequest.name" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("common.category")}</span>
                {getSortIcon("CategoryDocRequest.name")}
              </button>
              
              {/* Bouton de tri par date de création */}
              {!renewable && (
                <button 
                  onClick={() => handleCustomSort("createdAt")}
                  className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                    sortField === "createdAt" 
                      ? "bg-blue-100 text-blue-700 border border-blue-200" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                  }`}
                >
                  <span>{t("customerRequests.creation")}</span>
                  {getSortIcon("createdAt")}
                </button>
              )}
              
              {/* Bouton de tri par date limite */}
              <button 
                onClick={() => handleCustomSort("dueDate")}
                className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                  sortField === "dueDate" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("customerRequests.date")}</span>
                {getSortIcon("dueDate")}
              </button>
              
              {/* Bouton de tri par statut */}
              <button 
                onClick={() => handleCustomSort("State.state")}
                className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                  sortField === "State.state" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("common.status")}</span>
                {getSortIcon("State.state")}
              </button>

              {/* Relance */}
              <button 
                onClick={() => handleCustomSort("refused")}
                className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                  sortField === "refused" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("partner.refused")}</span>
                {getSortIcon("refused")}
              </button>

              {/* Message */}
              <button 
                onClick={() => handleCustomSort("commentaryCount")}
                className={`flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 sm:px-4 ${
                  sortField === "commentaryCount" 
                    ? "bg-blue-100 text-blue-700 border border-blue-200" 
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                }`}
              >
                <span>{t("partner.message")}</span>
                {getSortIcon("commentaryCount")}
              </button>
            </div>
        </div>
      </div>
      
      {/* Table des requêtes */}
      <div className="min-h-0 w-full flex-1 scroll-pb-20 overflow-y-auto pb-20 sm:scroll-pb-3 sm:pb-3">
        {/* En-têtes de colonnes */}
        <div className="sticky top-0 z-10 hidden w-full grid-cols-9 border-b border-gray-200 bg-gray-50 py-3 text-sm font-semibold text-gray-700 md:grid">
          <div className="col-start-1 col-end-2 flex justify-start items-center ml-2">
            {t("common.id")}
          </div>
          <div className="col-start-2 col-end-3 flex justify-start items-center">
            {t("common.category")}
          </div>
          {!renewable ? (
            <div className="hidden lg:flex col-start-3 col-end-4 justify-start items-center">
              {t("customerRequests.creation")}
            </div>
          ) : (
            <div className="hidden lg:flex col-start-3 col-end-4 justify-start items-center">
              {t("partner.month")}
            </div>
          )}
          <div className="hidden lg:flex col-start-4 col-end-5 justify-start items-center">
            {t("customerRequests.limit")}
          </div>
          <div className="hidden sm:flex sm:col-start-5 sm:col-end-8 justify-start items-center">
            {t("customerRequests.description")}
          </div>
          {!affectedUsers && (
            <>
              <div className="hidden sm:flex sm:col-start-8 sm:col-end-9 justify-start items-center">
                {t("customerRequests.requester")}
              </div>
              <div className="col-start-9 col-end-10 flex justify-start items-center">
                <div className="grid grid-cols-2 w-full">
                  <div className="hidden lg:flex lg:col-start-1 lg:col-end-2 mr-2">
                    {t("customerRequests.docs")}
                  </div>
                  <div className="col-start-1 lg:col-start-2 col-end-3 mr-2 flex items-center">
                    <span className="ml-6">{t("common.status")}</span>
                    {editStatus && (
                      <input
                        type="checkbox"
                        className="ml-4 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        checked={requestIds?.length === requests.length}
                        onChange={handleAllCheckboxChange}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Corps de la table */}
        <div className="w-full">
          {sortedData
            ?.filter(
              (request) =>
                displayAllRequests ||
                request.isPeriodClosed === null ||
                request.isPeriodClosed === false
            )
            .map((request: any) => (
              <div
                key={request?.id}
                className="w-full hover:bg-blue-50 transition-all duration-200 border-b border-gray-100"
              >
                <button
                  type="button"
                  className="flex w-full cursor-pointer flex-col gap-3 p-4 text-left transition-colors duration-200 hover:bg-blue-50 md:grid md:grid-cols-9 md:gap-0"
                  onClick={() => {
                    setOpenDetails(true);
                    setRequestId(request?.id);
                    setOpenDocuments("Documents");
                  }}
                >
                  {/* ID et indicateurs */}
                  <div className="flex items-center gap-2 md:col-start-1 md:col-end-2">
                    <div className="font-medium text-gray-900">
                      {request?.fakeId}
                    </div>
                    <div className="flex items-center gap-1">
                      {request?.refused === true && (
                        <div
                          className="text-lg"
                          onMouseEnter={(e) => {
                            if (request?.refused) {
                              handleMouseEnter(t("customerRequests.refused"), e);
                            }
                          }}
                          onMouseLeave={
                            request?.refused ? handleMouseLeave : undefined
                          }
                        >
                          📌
                        </div>
                      )}
                      {Object.keys(request?.reminder || {}).length > 0 && (
                        <div
                          className="text-sm"
                          onMouseEnter={(e) => {
                            if (Object.keys(request?.reminder || {}).length > 0) {
                              handleMouseEnter(
                                t("customerRequests.relaunched", {
                                  date: dateFormat(request.reminder?.date),
                                  user: request?.reminder?.user,
                                }),
                                e
                              );
                            }
                          }}
                          onMouseLeave={
                            request?.reminder ? handleMouseLeave : undefined
                          }
                        >
                          📩
                        </div>
                      )}
                      {request?.chatRead === false && (
                        <div className="rounded-full w-2 h-2 bg-red-600"></div>
                      )}
                      {request?.commentaryCount !== 0 && (
                        <div className="flex relative">
                          <img
                            src={message}
                            alt="message"
                            className="h-4 w-4"
                          />
                          <div className="rounded-full h-4 w-4 bg-gray-500 text-white text-xs flex justify-center items-center absolute -top-1 -right-3">
                            {request?.commentaryCount}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Catégorie */}
                  <div className="flex items-center md:col-start-2 md:col-end-3">
                    <div className="text-gray-700 font-medium">
                      {request?.CategoryDocRequest?.name}
                    </div>
                  </div>

                  {/* Date de création ou mois */}
                  <div className="hidden items-center lg:col-start-3 lg:col-end-4 lg:flex">
                    <div className="text-gray-600 text-sm">
                      {!renewable
                        ? dateFormat(request?.createdAt)
                        : request?.nameMissionPeriod}
                    </div>
                  </div>

                  {/* Date limite */}
                  <div className="hidden items-center lg:col-start-4 lg:col-end-5 lg:flex">
                    <div className="text-gray-600 text-sm">
                      {dateFormat(request?.dueDate)}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="flex items-center md:col-start-5 md:col-end-8">
                    <div className="text-gray-700 text-sm line-clamp-2">
                      {request?.description}
                    </div>
                  </div>

                  {affectedUsers ? (
                    /* Affectation multiple */
                    <div className="flex items-center justify-end border-t border-gray-100 pt-3 md:col-start-8 md:col-end-10 md:justify-center md:border-t-0 md:pt-0">
                      <input
                        type="checkbox"
                        name="affected"
                        className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        checked={docRequestUserAssign?.includes(request.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleRequestAssignment(request.id);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      {/* Demandeur */}
                      <div className="flex items-center md:col-start-8 md:col-end-9 md:mr-2">
                        <span className="mr-2 text-xs font-semibold text-gray-500 md:hidden">
                          {t("customerRequests.requester")}:
                        </span>
                        <div className="text-gray-600 text-sm">
                          {request?.User}
                        </div>
                      </div>

                      {/* Documents et Statut */}
                      <div className="flex items-center justify-between border-t border-gray-100 pt-3 md:col-start-9 md:col-end-10 md:border-t-0 md:pt-0">
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-gray-600">
                            <img
                              src={PaperClip}
                              alt="paperclip"
                              className="w-4 h-4"
                            />
                            <span className="text-sm">{request?.documentCount}</span>
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 items-center gap-2">
                          {/* Indicateur de statut */}
                          <div className="col-start-1 col-end-2 w-full flex justify-start items-center mr-5">
                          <div
                            className={`w-4 h-4 rounded-full border-2  ${
                              request?.State?.id === 1
                              ? "border-yellow-400 bg-yellow-400"
                              : request?.State?.id === 2
                              ? "border-blue-400 bg-blue-400"
                              : request?.State?.id === 3
                              ? "border-green-400 bg-green-400"
                              : request?.State?.id === 4
                              ? "border-red-500 bg-red-500"
                              : "border-gray-300 bg-gray-300"
                              }`}
                              ></div>
                          </div>
                          
                          {/* Checkbox pour édition */}
                          {editStatus && (
                            <input
                              type="checkbox"
                              name={request?.id}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 col-start-2 col-end-3"
                              checked={requestIds?.includes(request.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange(request?.id);
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </button>
              </div>
            ))}
        </div>

        {/* Message si aucune requête */}
        {(!sortedData || sortedData.length === 0) && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("dashboard.noRequestTitle")}</h3>
            <p className="text-gray-500">{t("partner.noRequestText")}</p>
          </div>
        )}
      </div>

      {/* Tooltip */}
      {tooltipState.content && (
        <div
          className="bg-gray-800 text-white p-3 rounded-lg text-sm absolute z-50 w-auto max-w-[250px] shadow-lg"
          style={{
            top: tooltipState.position.y,
            left: tooltipState.position.x,
          }}
        >
          {tooltipState.content}
        </div>
      )}
    </div>
  );
};

export default PartnerRequestTable;
